import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUserProfile } from '../utils/userApi';
import { setUserId, setUserProperties, resetUser } from '../utils/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = useCallback(async (authToken) => {
    try {
      const profileData = await getUserProfile(authToken);
      if (profileData) {
        setUser(prevUser => ({
          ...prevUser,
          generations_remaining: profileData.generations_remaining,
          feedback_count: profileData.feedback_count
        }));
        // Update localStorage
        const updatedUser = {
          ...JSON.parse(localStorage.getItem('user') || '{}'),
          generations_remaining: profileData.generations_remaining,
          feedback_count: profileData.feedback_count
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      // Don't fail login if profile fetch fails
    }
  }, []);

  useEffect(() => {
    // Check for existing authentication on app load
    const savedToken = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');
    
    if (savedToken && savedUser) {
      setToken(savedToken);
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      // So Amplitude attributes events to this user (not Anonymous)
      setUserId(parsedUser?.id);
      setUserProperties({ email: parsedUser?.email, name: parsedUser?.name });
      // Fetch latest user profile including generations_remaining
      fetchUserProfile(savedToken);
    }
    setLoading(false);
  }, [fetchUserProfile]);

  const login = async (userInfo) => {
    try {
      setLoading(true);
      
      console.log('Attempting login with userInfo:', { ...userInfo, credential: '***' });
      
      // Send user info to backend for authentication
      // Try proxy first (from package.json), fallback to direct URL
      const apiUrl = '/api/auth/google/';
      console.log('Calling API:', apiUrl, '(using proxy from package.json)');
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: userInfo.credential,
          email: userInfo.email,
          name: userInfo.name,
          given_name: userInfo.given_name,
          family_name: userInfo.family_name,
          picture: userInfo.picture,
          sub: userInfo.sub
        }),
      }).catch((fetchError) => {
        console.error('Fetch error:', fetchError);
        throw new Error(`Network error: ${fetchError.message}. Make sure the backend server is running on http://localhost:8000`);
      });

      console.log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }
        console.error('Auth error response:', errorData);
        throw new Error(errorData.error || `Authentication failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log("AUTH RESPONSE DATA:", data);
      
      if (!data.token || !data.user) {
        throw new Error('Invalid response from server: missing token or user data');
      }
      
      // Store authentication data
      setToken(data.token);
      setUser(data.user);
      setUserId(data.user?.id);
      setUserProperties({ email: data.user?.email, name: data.user?.name });
      // Persist to localStorage
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Fetch latest user profile including generations_remaining
      await fetchUserProfile(data.token);
      
      console.log('Login successful!');
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    resetUser();
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  };

  const isAuthenticated = useCallback(() => {
    return !!token && !!user;
  }, [token, user]);

  const refreshUserProfile = useCallback(async () => {
    if (token) {
      await fetchUserProfile(token);
    }
  }, [token, fetchUserProfile]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated,
    refreshUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
